import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import useApi from "components/Utils/api";
import toast from "react-hot-toast";
import Icon from "@mui/material/Icon";
import MDInput from "components/MDInput";
import MapDeviceDialog from "./components/MapDeviceDialog";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import AddDeviceDialog from "./components/AddDeviceDialog";
import StatusToggleButton from "./components/StatusToggleButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDTypography from "components/MDTypography";
import Tooltip from "@mui/material/Tooltip";

// Separate component for the action cell with prop validation
const ActionCell = ({ row, onRefresh, onEdit }) => {
  const [open, setOpen] = useState(false);
  const device = row.original;
  const [confirmUnmap, setConfirmUnmap] = useState(false);
  const [isUnmapping, setIsUnmapping] = useState(false);
  const api = useApi();

  const handleSuccess = () => {
    setOpen(false);
    onRefresh(); // Refresh the devices list after successful mapping
  };

  const handleUnmapDevice = async () => {
    setIsUnmapping(true);
    try {
      const response = await api.post("devices/unmap", {
        tid: device.mappedTerminalid,
        deviceSerialNo: device.serialNo
      });

      if (response.data.message) {
        toast.success(response.data.message);
        setConfirmUnmap(false);
        onRefresh(); // Refresh the devices list after unmapping
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to unmap device");
    } finally {
      setIsUnmapping(false);
    }
  };

  // Determine if the device is mapped
  const isMapped = !!device.mappedTerminalid || !!device.mappedTerminalName;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {/* Edit button - with tooltip if device is mapped */}
      <Tooltip 
        title={isMapped ? "Unmap device first to edit" : ""}
        placement="top"
      >
        <span>
          <MDButton
            variant="text"
            color="warning"
            onClick={() => onEdit(device)}
            startIcon={<Icon>edit</Icon>}
            size="small"
            disabled={isMapped}
          >
            Edit
          </MDButton>
        </span>
      </Tooltip>

      {!isMapped ? (
        <>
          <MDButton
            variant="text"
            color="info"
            onClick={() => setOpen(true)}
            startIcon={<Icon>link</Icon>}
          >
            Map
          </MDButton>

          <MapDeviceDialog
            open={open}
            onClose={() => setOpen(false)}
            device={device}
            onSuccess={handleSuccess}
          />
        </>
      ) : (
        <>
          <Chip
            icon={<Icon>check_circle</Icon>}
            label="Mapped"
            color="success"
            size="small"
            variant="outlined"
          />
          <MDButton
            variant="text"
            color="error"
            onClick={() => setConfirmUnmap(true)}
            startIcon={<Icon>link_off</Icon>}
            size="small"
          >
            Unmap
          </MDButton>

          <Dialog
            open={confirmUnmap}
            onClose={() => setConfirmUnmap(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>
              <MDTypography variant="h6">Confirm Unmap</MDTypography>
            </DialogTitle>
            <DialogContent>
              <MDTypography variant="body2">
                Are you sure you want to unmap device {device.deviceName} ({device.serialNo}) from terminal {device.mappedTerminalName}?
              </MDTypography>
            </DialogContent>
            <DialogActions>
              <MDButton
                onClick={() => setConfirmUnmap(false)}
                color="secondary"
                variant="outlined"
                disabled={isUnmapping}
              >
                Cancel
              </MDButton>
              <MDButton
                onClick={handleUnmapDevice}
                color="error"
                variant="gradient"
                disabled={isUnmapping}
              >
                {isUnmapping ? <CircularProgress size={20} color="inherit" /> : "Unmap Device"}
              </MDButton>
            </DialogActions>
          </Dialog>
        </>
      )}
    </div>
  );
};

ActionCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      mappedTerminalid: PropTypes.number,
      mappedTerminalName: PropTypes.string,
      serialNo: PropTypes.string,
      deviceName: PropTypes.string,
      model: PropTypes.string,
      status: PropTypes.number
    }).isRequired
  }).isRequired,
  onRefresh: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired
};

// Add this prop types definition for the Cell component
const StatusCellPropTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      serialNo: PropTypes.string.isRequired,
      status: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  onRefresh: PropTypes.func.isRequired,
};

// Pass onRefresh to StatusCell
const StatusCell = ({ row, onRefresh }) => (
  <StatusToggleButton
    type="device"
    id={row.original.serialNo}
    currentStatus={row.original.status}
    onSuccess={onRefresh}
  />
);

StatusCell.propTypes = StatusCellPropTypes;

function Devices() {
  const [devices, setDevices] = useState([]);
  const [searchSerialNo, setSearchSerialNo] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const api = useApi();
  const [addDeviceDialogOpen, setAddDeviceDialogOpen] = useState(false);
  const [updateDeviceDialogOpen, setUpdateDeviceDialogOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);

  const getDevices = () => {
    const params = searchSerialNo ? { serialNo: searchSerialNo } : {};
    api.get("devices", { params })
      .then((response) => {
        if (response.data.data) {
          setDevices(response.data.data);
        } else {
          toast.error(response.data.message || "Failed to fetch device records");
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        toast.error(error.response?.data?.message || "An error occurred while fetching device records");
      });
  };

  useEffect(() => {
    getDevices();
  }, []); // Initial load

  const handleSearch = (e) => {
    e.preventDefault();
    getDevices();
  };

  const validateCSVFile = (file) => {
    if (!file) {
      toast.error("Please select a file to upload.");
      return false;
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error("Please upload a valid CSV file.");
      return false;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      toast.error("File size should not exceed 5MB.");
      return false;
    }

    return true;
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    
    if (!validateCSVFile(file)) {
      event.target.value = '';
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post("devices/upload", formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.message) {
        toast.success(response.data.message);
        getDevices(); // Refresh the table
      }
    } catch (error) {
      if (error.response && error.response.status === 403) {
        toast.error("You do not have permission to perform this action.");
      } else {
        toast.error(error.response?.data?.error || "Failed to upload devices");
      }
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };
  const handleDownloadSample = () => {
    const sampleFileUrl = '/samples/device_sample.csv';
    const a = document.createElement('a');
    a.href = sampleFileUrl;
    a.download = 'device_sample.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleEditDevice = (device) => {
    setSelectedDevice(device);
    setUpdateDeviceDialogOpen(true);
  };
  const columns = [
    { Header: "Serial No", accessor: "serialNo", width: "20%", align: "left" },
    { Header: "Device Name", accessor: "deviceName", width: "20%", align: "left" },
    { Header: "Model", accessor: "model", width: "15%", align: "left" },
    { Header: "Mapped Terminal Id", accessor: "mappedTerminalid", width: "15%", align: "left" },
    { Header: "Mapped Terminal MID", accessor: "mappedTerminalMID", width: "20%", align: "left" },
    {
      Header: "Status",
      accessor: "status",
      width: "10%",
      align: "center",
      Cell: (props) => <StatusCell {...props} onRefresh={getDevices} />
    },
    {
      Header: "Action",
      accessor: "action",
      width: "10%",
      align: "center",
      Cell: (props) => <ActionCell {...props} onRefresh={getDevices} onEdit={handleEditDevice} />
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid container spacing={2} style={{ padding: "0 24px", marginBottom: "24px" }}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <form onSubmit={handleSearch}>
                <MDInput
                  label="Search by Serial No"
                  value={searchSerialNo}
                  onChange={(e) => setSearchSerialNo(e.target.value)}
                  fullWidth
                />
              </form>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleSearch}
                startIcon={<Icon>search</Icon>}
              >
                Search
              </MDButton>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={6} container justifyContent="flex-end" spacing={1}>
              <Grid item>
                <MDButton
                  variant="gradient"
                  onClick={handleDownloadSample}
                  color="dark"
                  startIcon={<Icon>download</Icon>}
                >
                  Sample Format
                </MDButton>
              </Grid>
              <Grid item>
                <MDButton
                  variant="gradient"
                  onClick={() => setAddDeviceDialogOpen(true)}
                  color="info"
                  startIcon={<Icon>add</Icon>}
                >
                  Add Serial
                </MDButton>
              </Grid>
              <Grid item>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                />
                <MDButton
                  variant="gradient"
                  onClick={triggerFileInput}
                  color="success"
                  disabled={isUploading}
                  startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <Icon>upload_file</Icon>}
                >
                  {isUploading ? "Uploading..." : "Upload Serials"}
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={12}>
            <Card>
              <DataTable
                table={{ columns, rows: devices }}
                showTotalEntries={true}
                isSorted={true}
                noEndBorder
                entriesPerPage={true}
                canSearch={true}
              />
            </Card>
          </Grid>
        </Grid>
      </MDBox>      <AddDeviceDialog
        open={addDeviceDialogOpen}
        onClose={() => setAddDeviceDialogOpen(false)}
        onSuccess={() => {
          setAddDeviceDialogOpen(false);
          getDevices(); // Refresh the devices list
        }}
      />
      <AddDeviceDialog
        open={updateDeviceDialogOpen}
        onClose={() => setUpdateDeviceDialogOpen(false)}
        onSuccess={() => {
          setUpdateDeviceDialogOpen(false);
          setSelectedDevice(null);
          getDevices(); // Refresh the devices list
        }}
        device={selectedDevice}
        isUpdateMode={true}
      />
      <Footer />
    </DashboardLayout>
  );
}

export default Devices; 